// CarrierResponsesTable.jsx
// -----------------------------------------------------------------------------
// - Renders a summary of carrier responses for a Tender.
// - Spot Tender:
//     * Simple vertical list of carriers with a checkbox (has quote?) and an
//       eye icon to open the Carrier Detail view.
// - Non-Spot Tender:
//     * Table layout where rows are carriers and columns are phases,
//       with checkboxes indicating whether each carrier quoted each phase.
// - The component is responsive; styles and horizontal scroll are handled
//   in CarrierResponsesTable.css.
// - For Non-Spot, the selected phase is part of the GLOBAL page state.
//   This component must both reflect and update that global phase selection.
// -----------------------------------------------------------------------------

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { MCheckBox } from "src/M_Components";
import { useTranslation } from "react-i18next";
import "./CarrierResponsesTable.css";

/**
 * Reads a normalized entity id from multiple possible payload shapes.
 *
 * @param {object} obj - Source object.
 * @returns {number|string|null} Normalized id.
 */
const getEntityId = (obj) =>
  obj?.CarrierId ?? obj?.carrierId ?? obj?.Id ?? obj?.id ?? null;

/**
 * Reads a normalized phase id from a response payload.
 *
 * @param {object} response - Quote/response payload.
 * @returns {number|string|null} Phase id.
 */
const getResponsePhaseId = (response) =>
  response?.TenderPhaseId ?? response?.tenderPhaseId ?? null;

/**
 * Reads a normalized carrier id from a response payload.
 *
 * @param {object} response - Quote/response payload.
 * @returns {number|string|null} Carrier id.
 */
const getResponseCarrierId = (response) =>
  response?.CarrierId ?? response?.carrierId ?? null;

/**
 * Reads a normalized carrier display name.
 *
 * @param {object} carrier - Carrier payload.
 * @returns {string} Carrier name.
 */
const getCarrierName = (carrier) =>
  carrier?.Name ?? carrier?.name ?? carrier?.CarrierName ?? carrier?.carrierName ?? "";

/**
 * Reads a normalized phase display name.
 *
 * @param {object} phase - Phase payload.
 * @returns {string} Phase name.
 */
const getPhaseName = (phase) =>
  phase?.Name ?? phase?.name ?? `Phase ${phase?.Id ?? phase?.id ?? ""}`;

/**
 * Checks whether a carrier was invited to a specific phase.
 *
 * Carriers without phase metadata remain visible in every phase for backwards
 * compatibility with legacy tender-wide carrier payloads.
 *
 * @param {object} carrier - Carrier payload.
 * @param {number|string|null} phaseId - Phase id.
 * @returns {boolean} True when the carrier belongs to the phase.
 */
const isCarrierInvitedInPhase = (carrier, phaseId) => {
  if (phaseId == null) return false;

  const phaseIds =
    carrier?.InvitedPhaseIds ??
    carrier?.invitedPhaseIds ??
    carrier?.TenderPhaseIds ??
    carrier?.tenderPhaseIds ??
    [];

  if (!Array.isArray(phaseIds) || phaseIds.length === 0) return true;

  return phaseIds.some((id) => String(id) === String(phaseId));
};

/**
 * Builds the stable lookup key for a carrier/phase quote pair.
 *
 * @param {number|string|null} carrierId - Carrier id.
 * @param {number|string|null} phaseId - Phase id.
 * @returns {string|null} Lookup key or null when incomplete.
 */
const buildPhaseQuoteKey = (carrierId, phaseId) => {
  if (carrierId == null || phaseId == null) return null;
  return `${String(carrierId)}::${String(phaseId)}`;
};

/**
 * Treats inactive quotes as non-existent so the UI matches the rest of the page filters.
 *
 * @param {object} response - Quote payload.
 * @returns {boolean} True when the quote should be considered active.
 */
const isActiveResponse = (response) => {
  const inactive = response?.Inactive ?? response?.inactive;
  return inactive === undefined || inactive === false;
};

export default function CarrierResponsesTable({
  tenderId, // Not used directly here, kept for potential future needs
  phases = [], // Array of phases (Non-Spot tenders only)
  isSpotTender, // Flag indicating Spot vs Non-Spot tender
  setActiveView, // Setter to switch between "inQuotation" and "carrier" views
  setSelectedCarrierId, // Setter to choose which carrier is being inspected
  setSelectedPhaseId, // Setter to keep the selected phase global for the page
  selectedPhaseId, // Current global selected phase
  responses = [], // Array of quotes/responses
  carriers = [], // Array of carrier objects
}) {
  const { t } = useTranslation();

  /**
   * Pre-indexes active quote existence by carrier and by carrier/phase.
   *
   * This avoids scanning the full response collection for every rendered cell.
   */
  const quoteLookup = useMemo(() => {
    const carrierIds = new Set();
    const phaseKeys = new Set();

    for (const response of responses || []) {
      if (!isActiveResponse(response)) continue;

      const carrierId = getResponseCarrierId(response);
      if (carrierId == null) continue;

      carrierIds.add(String(carrierId));

      const phaseId = getResponsePhaseId(response);
      const key = buildPhaseQuoteKey(carrierId, phaseId);
      if (key) phaseKeys.add(key);
    }

    return { carrierIds, phaseKeys };
  }, [responses]);

  /**
   * Checks whether a carrier has any active quote.
   *
   * @param {number|string|null} carrierId - Carrier id.
   * @returns {boolean} True when at least one quote exists.
   */
  const hasCarrierQuote = (carrierId) => {
    if (carrierId == null) return false;
    return quoteLookup.carrierIds.has(String(carrierId));
  };

  /**
   * Checks whether a carrier has an active quote for a specific phase.
   *
   * @param {number|string|null} carrierId - Carrier id.
   * @param {number|string|null} phaseId - Phase id.
   * @returns {boolean} True when a quote exists.
   */
  const hasCarrierQuoteInPhase = (carrierId, phaseId) => {
    const key = buildPhaseQuoteKey(carrierId, phaseId);
    return key ? quoteLookup.phaseKeys.has(key) : false;
  };

  /**
   * Updates the GLOBAL selected phase.
   *
   * @param {number|string|null} phaseId - Phase id to select globally.
   * @returns {void}
   */
  const selectGlobalPhase = (phaseId) => {
    if (typeof setSelectedPhaseId === "function") {
      setSelectedPhaseId(phaseId ?? null);
    }
  };

  /**
   * Opens carrier detail for the currently selected GLOBAL phase.
   *
   * @param {number|string|null} carrierId - Carrier id.
   * @param {boolean} canOpen - Whether the carrier belongs to the selected phase.
   * @returns {void}
   */
  const openCarrierDetailForSelectedPhase = (carrierId, canOpen) => {
    if (carrierId == null || !canOpen) return;

    setSelectedCarrierId?.(carrierId);
    setActiveView?.("carrier");
  };

  // ==========================================================================
  // Spot Tender Layout
  // ==========================================================================
  if (isSpotTender) {
    return (
      <div className="crt-root">
        <span className="crt-title">{t("Carrier Responses")}</span>

        <div className="crt-spot-list">
          {carriers.map((carrier) => {
            const carrierId = getEntityId(carrier);
            const carrierName = getCarrierName(carrier);

            const hasQuote = hasCarrierQuote(carrierId);
            const eyeDisabled = carrierId == null;

            return (
              <div key={carrierId} className="crt-spot-row">
                <span className="crt-spot-carrier-name">{carrierName}</span>

                <MCheckBox checked={hasQuote} disabled />

                <i
                  className={`pi pi-eye crt-eye-icon ${eyeDisabled ? "crt-eye-icon--disabled" : ""
                    }`}
                  onClick={() => {
                    if (eyeDisabled) return;
                    setSelectedCarrierId?.(carrierId);
                    setActiveView?.("carrier");
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ==========================================================================
  // Non-Spot Tender Layout
  // - Phase selection is GLOBAL.
  // - Header click selects phase globally.
  // - Cell click also selects phase globally.
  // - Eye opens against the currently selected global phase, even without quotes.
  // ==========================================================================
  return (
    <div className="crt-root crt-root--nonspot">
      <span className="crt-title">{t("Carrier Responses")}</span>

      <div className="crt-table-wrapper">
        <table className="crt-table">
          <thead>
            <tr className="crt-header-row">
              <th className="crt-th crt-th-carrier">{t("Carrier")}</th>

              {phases.map((phase) => {
                const phaseId = getEntityId(phase);
                const isSelected =
                  selectedPhaseId != null &&
                  String(selectedPhaseId) === String(phaseId);

                return (
                  <th
                    key={phaseId}
                    className={`crt-th crt-th-phase ${isSelected ? "crt-th-phase--selected" : ""
                      }`}
                    onClick={() => selectGlobalPhase(phaseId)}
                  >
                    {getPhaseName(phase)}
                  </th>
                );
              })}

              <th className="crt-th crt-th-actions" />
            </tr>
          </thead>

          <tbody>
            {carriers.map((carrier) => {
              const carrierId = getEntityId(carrier);
              const carrierName = getCarrierName(carrier);

              const invitedInSelectedPhase = isCarrierInvitedInPhase(
                carrier,
                selectedPhaseId
              );

              const eyeDisabled =
                carrierId == null || selectedPhaseId == null || !invitedInSelectedPhase;

              return (
                <tr key={carrierId} className="crt-row">
                  <td className="crt-td crt-td-carrier">{carrierName}</td>

                  {phases.map((phase) => {
                    const phaseId = getEntityId(phase);
                    const invitedInPhase = isCarrierInvitedInPhase(carrier, phaseId);
                    const hasPhaseQuote =
                      invitedInPhase && hasCarrierQuoteInPhase(carrierId, phaseId);
                    const isSelected =
                      selectedPhaseId != null &&
                      String(selectedPhaseId) === String(phaseId);

                    return (
                      <td
                        key={phaseId}
                        className={`crt-td crt-td-center crt-td-phase ${isSelected ? "crt-td-phase--selected" : ""
                          }`}
                        onClick={() => selectGlobalPhase(phaseId)}
                      >
                        {invitedInPhase ? (
                          <MCheckBox checked={hasPhaseQuote} disabled />
                        ) : (
                          <span>-</span>
                        )}
                      </td>
                    );
                  })}

                  <td className="crt-td crt-td-center crt-td-actions">
                    <i
                      className={`pi pi-eye crt-eye-icon ${eyeDisabled ? "crt-eye-icon--disabled" : ""
                        }`}
                      onClick={() => {
                        openCarrierDetailForSelectedPhase(
                          carrierId,
                          !eyeDisabled
                        );
                      }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

CarrierResponsesTable.propTypes = {
  tenderId: PropTypes.number,
  phases: PropTypes.array,
  isSpotTender: PropTypes.bool,
  setActiveView: PropTypes.func,
  setSelectedCarrierId: PropTypes.func,
  setSelectedPhaseId: PropTypes.func,
  selectedPhaseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  responses: PropTypes.array,
  carriers: PropTypes.array,
};